import { makeRequest } from "./client";
import type {
  ChildExtracurricular,
  ChildFeeAccount,
  ChildReportCard,
  ChildSchoolFees,
  ChildStatus,
  GuardianRole,
  ParentChildGuardian,
} from "../../types/parentFamily";

/** Raw backend FamilyMemberView */
export interface FamilyMemberApi {
  memberId: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  gender?: string;
  grade?: string;
  classSection?: string;
  studentId?: string;
  notes?: string;
  status: ChildStatus;
  linkedCircleIds: string[];
  schoolFees?: ChildSchoolFees | null;
  feeAccounts?: ChildFeeAccount[];
  extracurriculars: ChildExtracurricular[];
  reportCards?: ChildReportCard[];
  createdAt: string;
  updatedAt: string;
  createdByUserId: string;
  guardians: ParentChildGuardian[];
  myRole?: GuardianRole;
}

export interface FamilyListResponse {
  message: string;
  organizationId: string;
  userId: string;
  members: FamilyMemberApi[];
  count: number;
}

export interface FamilyMemberResponse {
  message: string;
  organizationId: string;
  userId?: string;
  member: FamilyMemberApi;
}

export type FamilyMemberWriteBody = {
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  gender?: string;
  grade?: string;
  classSection?: string;
  studentId?: string;
  notes?: string;
  status?: ChildStatus;
  linkedCircleIds?: string[];
  schoolFees?: ChildSchoolFees | null;
  feeAccounts?: ChildFeeAccount[];
  extracurriculars?: ChildExtracurricular[];
  reportCards?: ChildReportCard[];
};

export interface FamilySearchIndexEntry {
  memberId: string;
  firstName: string;
  lastName: string;
  studentId?: string;
  grade?: string;
  classSection?: string;
  guardianUserIds: string[];
}

export interface FamilySearchIndexResponse {
  message: string;
  organizationId: string;
  entries: FamilySearchIndexEntry[];
  count: number;
}

export const familyApi = {
  getForUser: async (
    orgId: string,
    userId: string,
    token: string
  ): Promise<FamilyMemberApi[]> => {
    const response = await makeRequest<FamilyListResponse>(
      `/v1/org/${orgId}/users/${userId}/family`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response?.members || [];
  },

  getSearchIndex: async (
    orgId: string,
    token: string
  ): Promise<FamilySearchIndexEntry[]> => {
    const response = await makeRequest<FamilySearchIndexResponse>(
      `/v1/org/${orgId}/family/search-index`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response?.entries || [];
  },

  createMember: async (
    orgId: string,
    userId: string,
    token: string,
    body: FamilyMemberWriteBody
  ): Promise<FamilyMemberApi> => {
    const response = await makeRequest<FamilyMemberResponse>(
      `/v1/org/${orgId}/users/${userId}/family/members`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }
    );
    if (!response?.member) {
      throw new Error("Failed to create family member");
    }
    return response.member;
  },

  updateMember: async (
    orgId: string,
    memberId: string,
    token: string,
    body: Partial<FamilyMemberWriteBody>
  ): Promise<FamilyMemberApi> => {
    const response = await makeRequest<FamilyMemberResponse>(
      `/v1/org/${orgId}/family/members/${memberId}`,
      {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      }
    );
    if (!response?.member) {
      throw new Error("Failed to update family member");
    }
    return response.member;
  },

  deleteMember: async (
    orgId: string,
    memberId: string,
    token: string
  ): Promise<void> => {
    await makeRequest<{ message: string }>(
      `/v1/org/${orgId}/family/members/${memberId}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  },

  addGuardian: async (
    orgId: string,
    memberId: string,
    token: string,
    payload: { userId: string; role?: GuardianRole }
  ): Promise<FamilyMemberApi> => {
    const response = await makeRequest<FamilyMemberResponse>(
      `/v1/org/${orgId}/family/members/${memberId}/guardians`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      }
    );
    if (!response?.member) {
      throw new Error("Failed to link guardian");
    }
    return response.member;
  },

  removeGuardian: async (
    orgId: string,
    memberId: string,
    guardianUserId: string,
    token: string
  ): Promise<{ deletedMember: boolean }> => {
    const response = await makeRequest<{
      message: string;
      deletedMember: boolean;
    }>(
      `/v1/org/${orgId}/family/members/${memberId}/guardians/${guardianUserId}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return { deletedMember: response?.deletedMember === true };
  },
};
